import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';
import DateField from 'components/fields/DateField';
import TimeField from 'components/fields/TimeField';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import { useAppSelector, useAppDispatch } from 'hooks';
import useFormBinderDisabled from 'hooks/useFormBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { addFile, removeFile } from 'reducers/autoPersonalFormReducer';

import { FILE_TYPE } from 'variables/constants';
import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import { useCarriers } from 'hooks/use-carriers-hook';

import { useMarketApprovalResolver } from './hooks';
import {
  getHTMLId,
  useResolverModal,
  parseDateString,
  BINDING_AUTHORITY_MESSAGE,
  MARKET_MISMATCH_MESSAGE,
} from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation006ResolverProps extends ValidationResolverProps {
  /** Override for reuse as market-approval flow for 017 (Unrepaired damage). Defaults to 006. */
  validationId?: string;
  marketApprovalFileType?: number;
  modalTitle?: string;
  forceShow?: boolean;
  customAlertMessage?: string;
  hideAlertMessage?: boolean;
  validationTypeKey?: string;
  /** Force email approval and hide phone-call option (e.g. validation 152). */
  emailOnly?: boolean;
  allowPhoneApproval?: boolean;
  approvalNumberRequired?: boolean;
}

const DEFAULT_VALIDATION_ID = '006';
const FORM_STATE_KEY = 'autoPersonalForm';
const VEHICLE_PHOTOS_ITEM_KEY_SUFFIX = '-vehicle-photos';

const Validation006Resolver = ({
  section,
  compact = false,
  itemKey: itemKeyProp,
  validationId = DEFAULT_VALIDATION_ID,
  marketApprovalFileType = FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type,
  modalTitle = 'Market Approval Resolution',
  forceShow,
  customAlertMessage,
  hideAlertMessage = false,
  validationTypeKey,
  emailOnly: emailOnlyProp = false,
  allowPhoneApproval = false,
  approvalNumberRequired = false,
}: Validation006ResolverProps) => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const isMarketApprovalEmailEmlMsgEnabled = useFeatureFlag(
    'enable-market-approval-email-eml-msg',
  );
  const mandatoryFileValidations = getMandatoryFileValidations(FORM_STATE_KEY);
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = itemKeyProp ?? 'global';
  const is017 = validationId === '017';
  const usesSummaryOnly = is017 || validationId === '133b';
  const vehiclePhotosItemKey = `${itemKey}${VEHICLE_PHOTOS_ITEM_KEY_SUFFIX}`;

  const vehiclePhotosFile = useAppSelector((state) =>
    is017
      ? state.autoPersonalForm.files.find(
          (f: { validation_key: string; item_key: string }) =>
            f.validation_key === '017' && f.item_key === vehiclePhotosItemKey,
        )
      : undefined,
  );

  const { state, handlers, fieldIds, alertMessage, getCompactAlertStatus } =
    useMarketApprovalResolver({
      validationId,
      section,
      itemKey,
      formStateKey: FORM_STATE_KEY,
      showLOEOption: false,
      marketApprovalFileType,
      forceShow,
      customAlertMessage,
      remarksRequired: is017,
      marketRequired: true,
      emailOnly: emailOnlyProp,
      allowPhoneApproval,
      approvalNumberRequired,
    });

  const { carrierOptions } = useCarriers();

  const validateTextContext =
    validationTypeKey === 'remarks_market_approval'
      ? state.approvalContext
      : [alertMessage, state.approvalContext].filter(Boolean).join(' ');

  const htmlId = getHTMLId(validationId, itemKey);

  // Use shared modal hook for hash-based navigation
  const { handleModalClose } = useResolverModal(
    compact,
    Object.values(fieldIds),
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  useEffect(() => {
    if (state.emailOnly && state.show && !state.approvalMethod) {
      handlers.handleApprovalMethodChange('email');
    }
  }, [state.emailOnly, state.show, state.approvalMethod, handlers]);

  useEffect(() => {
    if (is017 && state.show) {
      dispatch(
        addFile({
          validation_key: '017',
          item_key: vehiclePhotosItemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: FILE_TYPE.TYPE_092_110.type,
          files: [],
          text: FILE_TYPE.TYPE_092_110.text,
          mandatory: mandatoryFileValidations.includes('017'),
        }),
      );
    } else if (is017) {
      dispatch(
        removeFile({
          validation_key: '017',
          item_key: vehiclePhotosItemKey,
        }),
      );
    }
  }, [
    is017,
    state.show,
    vehiclePhotosItemKey,
    section,
    mandatoryFileValidations,
    dispatch,
  ]);

  if (!state.show) return null;

  // Compact mode: show button that opens modal
  if (compact) {
    return (
      <>
        {/* Anchor divs for mandatory field navigation */}
        <div id={htmlId}></div>
        <div id={fieldIds.resolution}></div>
        <div id={fieldIds.approvalMethod}></div>
        <div id={fieldIds.remarks}></div>
        <div id={fieldIds.date}></div>
        <div id={fieldIds.time}></div>
        <div id={fieldIds.underwriter}></div>
        <div id={fieldIds.market}></div>
        <div id={fieldIds.approvalNumber}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{modalTitle}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation006Resolver
                  section={section}
                  compact={false}
                  itemKey={itemKey}
                  validationId={validationId}
                  marketApprovalFileType={marketApprovalFileType}
                  modalTitle={modalTitle}
                  forceShow={forceShow}
                  customAlertMessage={customAlertMessage}
                  hideAlertMessage={hideAlertMessage}
                  validationTypeKey={validationTypeKey}
                  emailOnly={emailOnlyProp}
                  allowPhoneApproval={allowPhoneApproval}
                  approvalNumberRequired={approvalNumberRequired}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      {/* Anchor divs for mandatory field navigation */}
      <div id={htmlId}></div>
      <div id={fieldIds.resolution}></div>
      <div id={fieldIds.approvalMethod}></div>
      <div id={fieldIds.remarks}></div>
      <div id={fieldIds.date}></div>
      <div id={fieldIds.time}></div>
      <div id={fieldIds.underwriter}></div>
      <div id={fieldIds.market}></div>
      <div id={fieldIds.approvalNumber}></div>
      {!hideAlertMessage && (
        <Alert status="warning" mt="10px" fontSize="14px">
          <AlertIcon />
          <ValidationMessage message={alertMessage} />
        </Alert>
      )}

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        {/* 017 only: Remarks (mandatory) + vehicle photos, shown before approval method */}
        {is017 && (
          <VStack spacing={4} align="stretch" mb="15px">
            <Box id={fieldIds.remarks}>
              <SharedTextFieldInput
                label="Remarks"
                value={state.remark?.text || ''}
                onChange={handlers.handleRemarksChange}
                borderColor={
                  !state.remark?.text || state.remark.text.trim().length === 0
                    ? 'red'
                    : 'inherit'
                }
                formStateKey={FORM_STATE_KEY}
                section={section}
              />
            </Box>
            {state.remark && (
              <ValidateText
                uuid={uuid}
                remark={state.remark}
                validationId={validationId}
                validationTypeKey={validationTypeKey}
                itemKey={itemKey}
                formStateKey={FORM_STATE_KEY}
                context={validateTextContext}
              />
            )}
            {vehiclePhotosFile && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb="10px">
                  6 photos required: 4 vehicle, 1 VIN, 1 sticker
                </Text>
                <ValidateFile
                  uuid={uuid}
                  file={vehiclePhotosFile}
                  validationId={validationId}
                  itemKey={vehiclePhotosItemKey}
                  formStateKey={FORM_STATE_KEY}
                />
              </Box>
            )}
          </VStack>
        )}

        {/* Approval Method selection */}
        {!state.emailOnly && (
          <FormControl mb="15px" id={fieldIds.approvalMethod}>
            <FormLabel fontWeight="semibold">Approval Method</FormLabel>
            <RadioGroup
              value={state.approvalMethod || ''}
              onChange={handlers.handleApprovalMethodChange}
              isDisabled={isBinderDisabled}
            >
              <Stack direction="row" spacing={5}>
                <Radio value="email">I got approval via email</Radio>
                <Radio value="phone_call">I got approval via phone call</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
        )}

        {/* Email path */}
        {state.approvalMethod === 'email' && state.file && (
          <Box>
            <Text fontSize="sm" color="gray.600" mb="10px">
              {isMarketApprovalEmailEmlMsgEnabled
                ? 'Upload market approval email as evidence (.eml or .msg only)'
                : 'Upload market approval email as evidence'}
            </Text>
            <ValidateFile
              uuid={uuid}
              file={state.file}
              validationId={validationId}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
            />
          </Box>
        )}

        {state.showPhoneCallFields && state.remark && (
          <VStack spacing={4} align="stretch" mt="10px">
            {(() => {
              const useRemarks =
                !usesSummaryOnly && state.approvalMethod === 'phone_call';
              return (
                <VStack spacing={4} align="stretch">
                  <Box id={useRemarks ? fieldIds.remarks : undefined}>
                    <SharedTextFieldInput
                      label={
                        useRemarks
                          ? 'Remarks'
                          : 'Summary of Market Conversation'
                      }
                      value={
                        useRemarks
                          ? state.remark.text || ''
                          : state.summaryOfMarketConversationText || ''
                      }
                      onChange={
                        useRemarks
                          ? handlers.handleRemarksChange
                          : handlers.handleSummaryChange
                      }
                      borderColor={
                        useRemarks
                          ? 'inherit'
                          : !state.summaryOfMarketConversationText ||
                              state.summaryOfMarketConversationText.trim()
                                .length === 0
                            ? 'red'
                            : 'inherit'
                      }
                      formStateKey={FORM_STATE_KEY}
                      section={section}
                    />
                  </Box>
                  {useRemarks && (
                    <ValidateText
                      uuid={uuid}
                      remark={state.remark}
                      validationId={validationId}
                      validationTypeKey={validationTypeKey}
                      itemKey={itemKey}
                      formStateKey={FORM_STATE_KEY}
                      context={validateTextContext}
                    />
                  )}
                </VStack>
              );
            })()}

            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <Box flex={1} id={fieldIds.date}>
                <DateField
                  label="Approval Date"
                  date={parseDateString(state.approvalDate)}
                  onChange={handlers.handleDateChange}
                  required
                  isDisabled={isBinderDisabled}
                  htmlID={fieldIds.date}
                />
                {state.approvalOutsideBindingAuthority && (
                  <Text fontSize="sm" color="red.500" mt="5px">
                    {BINDING_AUTHORITY_MESSAGE}
                  </Text>
                )}
              </Box>
              <Box flex={1} id={fieldIds.time}>
                <TimeField
                  label="Approval Time"
                  value={state.approvalTime || ''}
                  onChange={handlers.handleTimeChange}
                  required
                  isDisabled={isBinderDisabled}
                  htmlID={fieldIds.time}
                />
              </Box>
            </Stack>

            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <FormControl isRequired flex={1} id={fieldIds.underwriter}>
                <FormLabel>Underwriter Name</FormLabel>
                <Input
                  variant="main"
                  value={state.underwriterName || ''}
                  onChange={handlers.handleUnderwriterNameChange}
                  disabled={isBinderDisabled}
                  borderColor={
                    !state.underwriterName ||
                    state.underwriterName.trim().length === 0
                      ? 'red'
                      : 'inherit'
                  }
                />
              </FormControl>

              <FormControl isRequired flex={1} id={fieldIds.market}>
                <FormLabel>Market</FormLabel>
                <Select
                  variant="main"
                  value={state.market || ''}
                  onChange={(e) => handlers.handleMarketChange(e.target.value)}
                  disabled={isBinderDisabled}
                >
                  <option value="">Select market...</option>
                  {carrierOptions.map((opt) => (
                    <option key={opt.value} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                {state.marketMismatch && (
                  <Text fontSize="sm" color="red.500" mt="5px">
                    {MARKET_MISMATCH_MESSAGE}
                  </Text>
                )}
              </FormControl>
            </Stack>

            <FormControl
              isRequired={approvalNumberRequired}
              id={fieldIds.approvalNumber}
            >
              <FormLabel>
                {approvalNumberRequired
                  ? 'Submission Number'
                  : 'Approval Number (Optional)'}
              </FormLabel>
              <Input
                variant="main"
                value={state.approvalNumber || ''}
                onChange={handlers.handleApprovalNumberChange}
                disabled={isBinderDisabled}
                borderColor={
                  approvalNumberRequired &&
                  (!state.approvalNumber ||
                    state.approvalNumber.trim().length === 0)
                    ? 'red'
                    : 'inherit'
                }
              />
            </FormControl>
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default Validation006Resolver;
